import * as React from 'react';

import {
  enumQuoteSortOption,
  enumQuoteStatus,
  QuoteSortOption,
} from '@rematter/frontend-lib-api-client';
import {
  Badge,
  Button,
  DataGrid,
  FloatingToolbar,
  ListScreenLayout,
  Pagination,
  type PaginationProps,
  useMemoDataGridRows,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucidePlusCircle, LucideTrash2, LucideX } from 'lucide-react';

import * as state from '../../_state/quotesList.state';
import { useTranslations } from '../../i18n/useTranslations';
import { brokerageRoutePaths } from '../../routes';
import { QuoteRecord } from '../../types/quote';

import { useQuoteListColumns } from './hooks/useQuoteListColumns';
import { QuoteCloneDialog } from './QuoteCloneDialog';
import { QuoteCreateDialog } from './QuoteCreateDialog';
import { QuoteDeleteDialog } from './QuoteDeleteDialog';
import { QuoteListFilters } from './QuoteListFilters';

import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

const COLUMN_TO_SORT_MAP: Record<
  string,
  { asc: QuoteSortOption; desc: QuoteSortOption }
> = {
  quoteNumber: {
    asc: enumQuoteSortOption.DocumentNumberAsc,
    desc: enumQuoteSortOption.DocumentNumberDesc,
  },
  type: {
    asc: enumQuoteSortOption.TypeAsc,
    desc: enumQuoteSortOption.TypeDesc,
  },
  status: {
    asc: enumQuoteSortOption.StatusAsc,
    desc: enumQuoteSortOption.StatusDesc,
  },
  validUntil: {
    asc: enumQuoteSortOption.ValidUntilAsc,
    desc: enumQuoteSortOption.ValidUntilDesc,
  },
};

//
// ⚛️ ⚛️ ⚛️ QUOTE LIST VIEW ⚛️ ⚛️ ⚛️
//
export const QuoteListView = () => {
  const t = useTranslations();
  const [, setLocation] = useModuleLocation();

  // Atoms
  const { data, isFetching, loadingState, error, refetch } = useAtomValue(
    state.$remoteQuotes,
  );
  const hasFiltersActive = useAtomValue(state.$filtersActive);
  const [selectedKeys, setSelectedKeys] = useAtom(state.$selectedKeys);
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useAtom(
    state.$isCreateDialogOpen,
  );
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useAtom(
    state.$isDeleteDialogOpen,
  );
  const [quotesToDelete, setQuotesToDelete] = useAtom(state.$quotesToDelete);
  const deleteQuote = useSetAtom(state.$deleteQuote);
  const setCurrentSorting = useSetAtom(state.$currentSorting);

  const toast = useToast();

  // Callbacks
  const handleRowClick = React.useCallback(
    (quote: QuoteRecord) => {
      setLocation(brokerageRoutePaths.quoteDetail, {
        params: { id: quote.id },
      });
    },
    [setLocation],
  );

  const handleSingleDelete = React.useCallback(
    (quote: QuoteRecord) => {
      setQuotesToDelete([
        {
          id: quote.id,
          quoteNumber: quote.quoteNumber,
          status: quote.status,
        },
      ]);
      setIsDeleteDialogOpen(true);
    },
    [setQuotesToDelete, setIsDeleteDialogOpen],
  );

  // Other data hooks
  const { columns, columnVisibilityState } = useQuoteListColumns({
    onRowClick: handleRowClick,
    onVoid: handleSingleDelete,
    onRefetch: () => refetch?.(),
  });

  const quotes = data?.quotes ?? [];
  const total = data?.totalCount ?? 0;
  const selectedRows = useMemoDataGridRows(quotes, selectedKeys);

  // Memoized values
  const emptyPanelProps = React.useMemo(
    () => ({
      title: t.quotes.list.empty.title,
      description: <span>{t.quotes.list.empty.description}</span>,
      action: (
        <Button
          onPress={() => setIsCreateDialogOpen(true)}
          variant="solid"
          color="primary"
          icon={<LucidePlusCircle />}
        >
          {t.quotes.list.createButton}
        </Button>
      ),
      centered: true,
    }),
    [t, setIsCreateDialogOpen],
  );

  const emptyResultsPanelProps = React.useMemo(
    () => ({
      title: t.quotes.list.emptyResults.title,
      description: t.quotes.list.emptyResults.description,
      centered: true,
    }),
    [t],
  );

  const handlePaginationChange = React.useCallback<
    NonNullable<PaginationProps['onChange']>
  >(
    ({ limit: newLimit, offset: newOffset }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );
  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setCurrentSorting([enumQuoteSortOption.CreatedAtDesc]);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          const sortOption =
            direction === 'ascending' ? sortMap.asc : sortMap.desc;
          setCurrentSorting([sortOption]);
        }
      }
      setOffset(0);
    },
    [setCurrentSorting, setOffset],
  );
  const handleClearSelection = React.useCallback(() => {
    setSelectedKeys(new Set());
  }, [setSelectedKeys]);
  const handleCreateQuote = React.useCallback(() => {
    setIsCreateDialogOpen(true);
  }, [setIsCreateDialogOpen]);
  const handleDeleteConfirm = React.useCallback(
    async (id: string) => {
      await deleteQuote({ id });
    },
    [deleteQuote],
  );
  const handleDeleteComplete = React.useCallback(() => {
    const isBulk = quotesToDelete.length > 1;
    if (isBulk) {
      toast.success(t.quotes.toast.bulkVoided(quotesToDelete.length));
    } else {
      toast.success(t.quotes.toast.voided);
    }
    setSelectedKeys(new Set());
    setIsDeleteDialogOpen(false);
    setQuotesToDelete([]);
  }, [
    quotesToDelete,
    toast,
    t,
    setSelectedKeys,
    setIsDeleteDialogOpen,
    setQuotesToDelete,
  ]);
  const handleBulkDelete = React.useCallback(() => {
    const quotesAllowedToDelete = selectedRows
      .filter(
        (row) =>
          row.status !== enumQuoteStatus.Expired &&
          row.status !== enumQuoteStatus.Converted &&
          row.status !== enumQuoteStatus.Rejected,
      )
      .map((row) => ({
        id: row.id,
        quoteNumber: row.quoteNumber,
        status: row.status,
      }));

    if (quotesAllowedToDelete.length > 0) {
      setQuotesToDelete(quotesAllowedToDelete);
      setIsDeleteDialogOpen(true);
    }
  }, [selectedRows, setQuotesToDelete, setIsDeleteDialogOpen]);
  const handleVoidCancel = React.useCallback(() => {
    setIsDeleteDialogOpen(false);
    setQuotesToDelete([]);
  }, [setIsDeleteDialogOpen, setQuotesToDelete]);

  return (
    <div className="@QuoteListView flex h-full flex-col">
      <ListScreenLayout
        filters={<QuoteListFilters />}
        actions={
          <Button
            onPress={handleCreateQuote}
            variant="solid"
            color="primary"
            icon={<LucidePlusCircle />}
          >
            {t.quotes.list.createButton}
          </Button>
        }
        footer={
          total > limit ? (
            <Pagination
              limit={limit}
              offset={offset}
              total={total}
              onChange={handlePaginationChange}
            />
          ) : undefined
        }
      >
        <DataGrid<QuoteRecord>
          isLoading={loadingState.isInitialLoading || isFetching}
          columns={columns}
          dataSource={quotes}
          rowKey="id"
          aria-label={t.quotes.list.ariaLabel}
          selectionMode="multiple"
          selectedKeys={selectedKeys}
          onSelectionChange={setSelectedKeys}
          onColumnSort={handleColumnSort}
          columnVisibilityState={columnVisibilityState}
          className="w-full text-sm"
          fullWidth
          persistColumnOrderKey="brokerage:quotes-list"
          hasError={!!error}
          errorPanelProps={
            error
              ? {
                  description: t.common.errors.loading('quotes', error.message),
                  centered: true,
                }
              : undefined
          }
          emptyPanelProps={
            hasFiltersActive ? emptyResultsPanelProps : emptyPanelProps
          }
        />
      </ListScreenLayout>

      <FloatingToolbar visible={selectedRows.length > 0}>
        <div className="flex flex-row items-center justify-between gap-3">
          <div className="flex flex-row items-center gap-2 text-sm">
            <Badge color="primary" size="small">
              {selectedRows.length}
            </Badge>{' '}
            {t.common.selected}
          </div>
          <div className="flex flex-row items-center gap-2">
            <Button
              variant="outline"
              icon={<LucideTrash2 />}
              isDisabled={selectedRows.length < 1}
              onPress={handleBulkDelete}
            >
              {t.common.actions.delete}
            </Button>
            <LucideX
              className="size-4 cursor-pointer text-neutral/80"
              onClick={handleClearSelection}
            />
          </div>
        </div>
      </FloatingToolbar>

      <QuoteCloneDialog />
      <QuoteCreateDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSuccess={() => refetch?.()}
      />

      {isDeleteDialogOpen && quotesToDelete.length > 0 && (
        <QuoteDeleteDialog
          quotes={quotesToDelete}
          onDelete={handleDeleteConfirm}
          onComplete={handleDeleteComplete}
          onCancel={handleVoidCancel}
        />
      )}
    </div>
  );
};
